/**
 * The predicted view: `catchUp(clone(confirmed), predictTime)`, then every pending intent through
 * `apply`. Rebuilt when `confirmed` or `pending` changes, when the next sapling or upgrade is due,
 * and every 20 seconds.
 */
import { apply, catchUp, cloneState, groupMask, nextDue } from '@bramvasterd/terrella-rules';

import type { Game } from '../game';

/** Server time to predict at: now, but never before the confirmed state's time. */
export function predictTime(game: Game): number {
  const confirmed = game.store.get().confirmed;
  const now = game.clock.serverNow();
  if (!confirmed) return now ?? 0;
  return Math.max(now ?? confirmed.time, confirmed.time);
}

export function rebuildView(game: Game): void {
  const s = game.store.get();
  if (!s.confirmed) {
    game.store.set({ view: null, groups: null, planetVersion: s.planetVersion + 1 });
    return;
  }
  let view = s.confirmed;
  if (s.predicting && s.me) {
    view = cloneState(s.confirmed);
    const t = predictTime(game);
    catchUp(view, t);
    for (const intent of s.pending) {
      if (intent.kind !== 'action') break; // dev commands wait for the server
      const result = apply(view, s.me.id, intent.action, t);
      if (!result.ok) break; // the server decides from here
    }
  }
  game.store.set({ view, groups: groupMask(view), planetVersion: s.planetVersion + 1 });
}

/**
 * Rebuilds the view on a timer: at the next sapling's due time, and at least every 20 seconds (the
 * HUD's timers and an extractor's stored count follow the rebuilds).
 */
export function startPredictTimer(game: Game): () => void {
  let timer = 0;
  const schedule = (): void => {
    clearTimeout(timer);
    const { view } = game.store.get();
    const now = game.clock.serverNow();
    let wait = 20_000;
    const soon = (at: number | null): void => {
      if (at !== null && now !== null && at > now)
        wait = Math.min(wait, Math.max(50, at - now + 20));
    };
    soon(view ? nextDue(view) : null);
    timer = window.setTimeout(() => {
      rebuildView(game);
      schedule();
    }, wait);
  };
  const unsubscribe = game.store.subscribe((state, previous) => {
    if (state.planetVersion !== previous.planetVersion || state.here !== previous.here) schedule();
  });
  schedule();
  return () => {
    clearTimeout(timer);
    unsubscribe();
  };
}
